import { Box, Button, ButtonBase, Checkbox } from "@mui/material";
import { FormControl, InputLabel, TextField } from "@mui/material";
import { LinearProgress } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { useCallback, useContext, useEffect, useState } from "react";
import React, { forwardRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { FileTrackLinkingModalProps } from "./types";
import { APP_BACKGROUND_COLOR } from "../../theme";
import { useStyles } from "../ExploreBase/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Track, TrackFile, TrackState } from "../../proto/api_pb";
import { GetTracksResponse } from "../../proto/api_pb";
import { SelectField } from "../../pages/ProfileSettings/types";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { RpcError } from "grpc-web";
import { fetchTracks, postTrackFile } from "../../services/trackService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { FileTrackLinkingModalStyles } from "./styles";
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const FileTrackLinkingModal: React.FC<FileTrackLinkingModalProps> = ({
  isFileTrackLinkingModalOpen,
  setIsFileTrackLinkingModalOpen,
  file,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [allTracksList, setAllTracksList] = useState<Track[]>([]);
  const [selectedTracksList, setSelectedTracksList] = useState<string[]>([]);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();
  const classes = useStyles();

  const handleChangeTracksList = (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const values = value as string[];
    const selectedTracksList = [...values];
    setSelectedTracksList(selectedTracksList);
  };

  const getAllTracks = useCallback(async () => {
    setLoading(true);
    const token = await getValidAccessToken();
    try {
      const response = (await fetchTracks(token, userId)) as GetTracksResponse;
      const filteredTracks = response
        .getTracksList()
        .map((track) => track.getTrack())
        .filter(
          (track): track is Track =>
            track?.getState() !== TrackState.TRACK_STATE_DELETED
        );
      setAllTracksList(filteredTracks);
      setLoading(false);
    } catch (e) {
      const error = e as RpcError;
      console.log(error.message);
      setLoading(false);
    }
  }, [getValidAccessToken, userId]);

  useEffect(() => {
    if (userId !== "") {
      getAllTracks();
    }
  }, [userId, getAllTracks]);

  const handleSubmit = async () => {
    const trackFile = new TrackFile();
    const matchingTrackIds: string[] = selectedTracksList.map(
      (selectedTrackName) => {
        const matchingTrack = allTracksList.find(
          (track) => track.getTitle() === selectedTrackName
        );
        return matchingTrack ? matchingTrack.getId() : "";
      }
    );
    trackFile.setTrackId(matchingTrackIds[0]);
    trackFile.setTrackIdsList(matchingTrackIds);
    trackFile.setCollaboratorsList(file.getCollaboratorsList());
    trackFile.setFileState(file.getFileState());
    trackFile.setFileUrl(file.getFileUrl());
    trackFile.setFilename(file.getFilename());
    trackFile.setId(file.getId());
    trackFile.setType(file.getType());
    trackFile.setTimestamp(file.getTimestamp());
    trackFile.setOwnerId(file.getOwnerId());
    trackFile.setLabel(file.getLabel());
    const postTrackFileResponse = await postTrackFile(
      trackFile,
      await getValidAccessToken()
    );
    if (postTrackFileResponse instanceof RpcError) {
      setIsFileTrackLinkingModalOpen(false);
      throw postTrackFileResponse;
    } else {
      console.log(postTrackFileResponse);
      setIsFileTrackLinkingModalOpen(false);
    }
  };

  return (
    <>
      <Dialog
        open={isFileTrackLinkingModalOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setIsFileTrackLinkingModalOpen(false)}
        aria-describedby="alert-dialog-slide-description"
      >
        <Box sx={{ backgroundColor: APP_BACKGROUND_COLOR, padding: "15px" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ButtonBase onClick={() => setIsFileTrackLinkingModalOpen(false)}>
              <CloseIcon />
            </ButtonBase>
          </Box>
          <DialogTitle variant="h5" sx={{ textAlign: "center" }}>
            Link File With Tracks
          </DialogTitle>
          <DialogContent>
            {loading && (
              <LinearProgress
                sx={{ marginTop: "20px", marginBottom: "20px" }}
              />
            )}
            {!loading && (
              <Box sx={{ marginBottom: "10px" }}>
                <FormControl fullWidth sx={{ borderColor: "white" }}>
                  <InputLabel sx={{ fontFamily: "Argumentum" }}>
                    {selectedTracksList.length > 0 ? "" : "Tracks"}
                  </InputLabel>
                  <SelectField
                    multiple
                    fullWidth
                    options={allTracksList.map((track) => track.getTitle())}
                    disableCloseOnSelect
                    value={selectedTracksList}
                    onChange={handleChangeTracksList}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} style={{ fontFamily: "Argumentum" }}>
                        <>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option}
                        </>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        style={{ width: "270px" }}
                        {...params}
                        label={""}
                        InputLabelProps={{ shrink: false }}
                        classes={{ root: classes.customTextField }}
                      />
                    )}
                  />
                </FormControl>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    onClick={handleSubmit}
                    sx={FileTrackLinkingModalStyles.addButton}
                  >
                    Link
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};
